import { FC, Suspense, lazy, useEffect, useState } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";

import { MainRoutes, ROUTES } from "common/config";
import { ApiStatus, ToastVariant } from "common/enums";
import { ROUTE_QUERY } from "common/config/route.config";
import useCustomTranslation from "common/hooks/useCustomTranslation";
import useAuthenticate from "common/hooks/useAuthenticate";
import useSessionTimeout from "common/hooks/useSessionTimeout";
import { useAppSelector } from "common/redux/core";
import { RootState, useAppDispatch } from "common/redux/core/root.reducer";
import {
  Loading,
  LoadingWithProgressBar,
  UnAuthorized
} from "modules/_shared/pages";

import { WelcomeAnimation } from "common/components-v2";
import AppToast from "common/components/custom/app-toast";
import ConcurrentSessionModal from "common/components/custom/concurrent-session-modal/ConcurrentSessionModal";
import SessionTimeoutTimerModal from "common/components/custom/session-timeout-timer-modal/SessionTimeoutTimerModal";
import SpinnerLoader from "common/components/custom/spinner-loader/SpinnerLoader";
import { LogoutType } from "common/enums/logout-type.enum";
import { AppStorageKey } from "common/models";
import { appActions } from "common/redux/app/app.slice";
import { userActions } from "common/redux/user";
import { useLogoutUserMutation } from "common/redux/user/mutations";
import { SessionStorageService } from "common/services/session-storage.service";
import { auth0SessionCache } from "common/services/auth0-session-cache";
import SignalR from "common/services/signalr-service";
import useProfile from "common/hooks/useProfile";
import { ENV_CONFIG } from "common/config/env.config";
import { useLazyGetPrivacyPolicyQuery } from "common/redux/app/queries";
import labelCollection from "modules/engagement-dashboard/config/langauge-keys.config";
import GrcSyncListener from "modules/engagement-dashboard/components/grc-sync-listener/GrcSyncListener";
import { useLazyGetRacmDetailsQuery } from "modules/engagement-dashboard/redux/queries";
import ClientDetails from "modules/home/pages/client-details/ClientDetails";
import Dashboard from "modules/home/pages/dashboard/Dashboard";

import "./AuthorizedLayout.scss";

const EngagementDashboard = lazy(
  () =>
    import("modules/engagement-dashboard/pages/engagement-dashboard/EngagementDashboard")
);

const EngagementDocuments = lazy(
  () =>
    import("modules/engagement-dashboard/pages/engagement-documents/EngagementArtifacts")
);

const ControlInitialization = lazy(
  () =>
    import("modules/engagement-dashboard/pages/control-initialization/ControlInitialization")
);

const ControlLibrary = lazy(
  () =>
    import("modules/engagement-dashboard/pages/control-library/ControlLibrary")
);

const ControlDashboard = lazy(
  () => import("modules/controls/pages/control-dashboard/ControlDashboard")
);

const UserManagement = lazy(
  () => import("modules/user-management/pages/user-management/UserManagement")
);

const ContactSupport = lazy(
  () => import("modules/contact-support/pages/contact-support/ContactSupport")
);

const PrivacyPolicy = lazy(
  () => import("modules/privacy-policy/pages/privacy-policy/PrivacyPolicy")
);

const AuthorizedLayout: FC = () => {
  const { isAuthReady, accessStatus } = useAuthenticate();
  const [acceptedPrivacyPolicy, setAcceptedPrivacyPolicy] = useState(true);
  const { id: userId } = useProfile();
  const { t } = useCustomTranslation(["engagementDashboard"]);
  useSessionTimeout();
  const dispatch = useAppDispatch();

  const [clientId, setClientId] = useState("");
  const [engagementId, setEngagementId] = useState("");
  const [isWelcomeAnimationDone, setIsWelcomeAnimationDone] = useState(() => {
    // Check if welcome animation was already shown in this session
    return (
      SessionStorageService.get(AppStorageKey.IS_WELCOME_ANIMATION_DONE) ===
      "true"
    );
  });

  const showTimerModal = useAppSelector(
    (state: RootState) => state.user.showSessionTimeoutTimer
  );
  const [showSessionTimeoutModal, setShowSessionTimeoutModal] =
    useState<boolean>(false);

  const [showConcurrentSessionModal, setShowConcurrentSessionModal] =
    useState<boolean>(false);

  const hasUserLoggedOut = useAppSelector(
    (state: RootState) => state.user.userLoggedOut
  );
  const userLogoutType = useAppSelector(
    (state: RootState) => state.user.logoutType
  );
  const navigate = useNavigate();

  const { isConcurrentSession, isForbiddenPage } = useAppSelector(
    (state: RootState) => state.app
  );

  const [
    getPrivacyPolicy,
    {
      data: privacyPolicyData,
      isSuccess: isPrivacyPolicySuccess,
      isFetching: isPrivacyPolicyFetching,
      isLoading: isPrivacyPolicyLoading
    }
  ] = useLazyGetPrivacyPolicyQuery();

  const [getRacmDetails] = useLazyGetRacmDetailsQuery();

  const showLoadingPage =
    accessStatus === ApiStatus.LOADING ||
    (accessStatus !== ApiStatus.ERROR &&
      (ENV_CONFIG.ENABLE_PAANS ?? false) === true &&
      !isPrivacyPolicySuccess);

  useEffect(() => {
    if (privacyPolicyData && isPrivacyPolicySuccess) {
      setAcceptedPrivacyPolicy(privacyPolicyData.hasAccepted);
      if (privacyPolicyData?.hasAccepted === false) {
        window.location.href = privacyPolicyData?.redirectUrl;
      }
    }
  }, [privacyPolicyData, isPrivacyPolicySuccess]);

  // get the client and engagement id from the URL hash as this is not rendered by anu route so useParams cant be used
  useEffect(() => {
    const pathSegments = window.location.hash.split("/");
    const clientIdIndex = pathSegments.indexOf("client") + 1;
    const engagementIdIndex = pathSegments.indexOf("engagement") + 1;

    if (clientIdIndex > 0 && clientIdIndex < pathSegments.length) {
      setClientId(pathSegments[clientIdIndex]);
    }

    if (engagementIdIndex > 0 && engagementIdIndex < pathSegments.length) {
      setEngagementId(pathSegments[engagementIdIndex]);
    } else {
      setEngagementId("");
    }
  }, [window.location.hash]);

  // Fetch RACM file details to determine whether to navigate to the ingestion or mapping screen when the RACM Mapping card is clicked, based on the file status.
  // Also validates the engagement - on error, show toast and redirect to engagement listing.
  useEffect(() => {
    if (clientId && engagementId) {
      getRacmDetails({
        clientId,
        engagementId
      })
        .unwrap()
        .catch((error: any) => {
          if (error?.status === 500) {
            dispatch(
              appActions.setToastDetails({
                isVisible: true,
                message: t(labelCollection.engagementInvalidOrNotFound),
                type: ToastVariant.ERROR
              })
            );
            navigate(
              ROUTES.HOME.CLIENT_DETAILS.replace(
                ROUTE_QUERY.CLIENT_ID,
                clientId
              )
            );
          }
        });
    }
  }, [clientId, engagementId]);

  useEffect(() => {
    if (accessStatus === ApiStatus.SUCCESS && isAuthReady) {
      // Check for paans privacy policy only for when ENABLE_PAANS is true in environment variables
      if (ENV_CONFIG.ENABLE_PAANS === true) {
        let acceptUrl = window.location.href.split("#")[1];
        if (acceptUrl == undefined || acceptUrl == "") {
          acceptUrl = ROUTES.HOME.INDEX;
        }
        getPrivacyPolicy({
          acceptUrl
        });
      }
    }
  }, [accessStatus, isAuthReady]);

  const [
    logoutUser,
    {
      data: logoutUserData,
      isSuccess: isLogoutUserSucess,
      isLoading: isLogoutUserLoading
    }
  ] = useLogoutUserMutation();

  useEffect(() => {
    if (hasUserLoggedOut) {
      logoutUser({});
    }
  }, [hasUserLoggedOut]);

  useEffect(
    () =>
      auth0SessionCache.onRemoteLogout(() => {
        SignalR.getInstance().stopConnection();
        sessionStorage.clear();
        navigate(MainRoutes.LOGOUT);
      }),
    [navigate]
  );

  useEffect(() => {
    if (isLogoutUserSucess) {
      SignalR.getInstance().stopConnection();
      sessionStorage.clear();
      localStorage.clear();
      auth0SessionCache.notifyLogout();
      setShowSessionTimeoutModal(false);
      if (
        userLogoutType == LogoutType.LOGOUT ||
        userLogoutType == LogoutType.SESSION_TIMEOUT ||
        userLogoutType == LogoutType.HARD_SESSION_TIMEOUT
      ) {
        navigate(MainRoutes.LOGOUT);
      }
    }
  }, [isLogoutUserSucess, logoutUserData]);

  useEffect(() => {
    setShowSessionTimeoutModal(showTimerModal);
  }, [showTimerModal]);

  useEffect(() => {
    if (!showSessionTimeoutModal) {
      dispatch(userActions.updateSessionTimeoutModalVisibility(false));
    }
  }, [showSessionTimeoutModal]);

  useEffect(() => {
    setShowConcurrentSessionModal(isConcurrentSession);
  }, [isConcurrentSession]);

  useEffect(() => {
    // this creates a new instance of singleton signalR connection
    // initialized here to have the connection established when the app is loaded
    if (accessStatus === ApiStatus.SUCCESS && userId) {
      const signalRInstance = SignalR.getInstance();
      // Re-enable connections that a previous logout may have stopped.
      signalRInstance.setIsAuthenticated(true);
      signalRInstance.establishConnection();
    }
  }, [accessStatus]);

  // Covers logout paths that navigate away without going through this layout's logout effect (e.g. Header sign-out).
  useEffect(
    () => () => {
      SignalR.getInstance().stopConnection();
    },
    []
  );

  return (
    <>
      {showConcurrentSessionModal && (
        <ConcurrentSessionModal
          showModal={showConcurrentSessionModal}
          onCloseClick={() => setShowConcurrentSessionModal(false)}
        />
      )}

      {showLoadingPage ? (
        <LoadingWithProgressBar />
      ) : (
        <>
          {(accessStatus === ApiStatus.ERROR || isForbiddenPage) && (
            <UnAuthorized />
          )}
          {accessStatus === ApiStatus.SUCCESS && acceptedPrivacyPolicy && (
            <>
              {isAuthReady &&
                (!isWelcomeAnimationDone ? (
                  <WelcomeAnimation
                    welcomeText={"EY.ai for Risk: Internal Controls"}
                    skip={false}
                    onAnimationComplete={() => {
                      SessionStorageService.set(
                        AppStorageKey.IS_WELCOME_ANIMATION_DONE,
                        "true"
                      );
                      setIsWelcomeAnimationDone(true);
                    }}
                  />
                ) : (
                  <>
                    {isLogoutUserLoading && (
                      <SpinnerLoader className="logout-loader" />
                    )}
                    <>
                      <AppToast />

                      {clientId && engagementId && (
                        <GrcSyncListener
                          clientId={clientId}
                          engagementId={engagementId}
                        />
                      )}
                      {showSessionTimeoutModal && (
                        <SessionTimeoutTimerModal
                          showModal={showSessionTimeoutModal}
                          onCloseClick={() => setShowSessionTimeoutModal(false)}
                        />
                      )}

                      <main className="app-layout">
                        {/* <SideNav collapsed={isSideNavCollapsed} /> */}
                        <div className="app-layout-wrapper">
                          <Suspense fallback={<Loading />}>
                            <Routes>
                              <Route
                                path={ROUTES.HOME.INDEX}
                                element={<Dashboard />}
                              />
                              <Route
                                path={ROUTES.HOME.CLIENT_DETAILS}
                                element={<ClientDetails />}
                              />
                              <Route
                                path={ROUTES.ENGAGEMENT.DASHBOARD}
                                element={<EngagementDashboard />}
                              />
                              <Route
                                path={ROUTES.ENGAGEMENT.CONTROL_INITIALIZATION}
                                element={<ControlInitialization />}
                              />
                              <Route
                                path={ROUTES.ENGAGEMENT.CONTROL_LIBRARY}
                                element={<ControlLibrary />}
                              />
                              <Route
                                path={ROUTES.ENGAGEMENT.DOCUMENTS}
                                element={<EngagementDocuments />}
                              />
                              <Route
                                path={ROUTES.ENGAGEMENT.CONTROLS}
                                element={<ControlDashboard />}
                              />
                              <Route
                                path={ROUTES.USER_MGMT.INDEX}
                                element={<UserManagement />}
                              />
                              <Route
                                path={ROUTES.CONTACT_SUPPORT.INDEX}
                                element={<ContactSupport />}
                              />
                              <Route
                                path={ROUTES.PRIVACY_POLICY.INDEX}
                                element={<PrivacyPolicy />}
                              />
                              <Route
                                path={ROUTES.DEFAULT}
                                element={<Navigate to={MainRoutes.HOME} />}
                              />
                              <Route
                                path={ROUTES.WILD}
                                element={<Navigate to={ROUTES.HOME.INDEX} />}
                              />
                            </Routes>
                          </Suspense>
                        </div>
                      </main>
                    </>
                  </>
                ))}
            </>
          )}
        </>
      )}
    </>
  );
};

export default AuthorizedLayout;
